import type { Announcement } from '../api/types';
import { sortAnnouncements, visibleAnnouncements } from './unread';

/**
 * Pure rules for the announcement popup (docs/42 §6, steward-app#2613). Data in, data out; the
 * component owns the timing and the DOM.
 */

/** id → ISO instant the person closed that announcement's popup. */
export type AnnouncementPopupDismissed = Record<string, string>;

/** Cap on the stored map. The preferences blob has one 8KB budget shared by every key (the
 *  `FEEDBACK_SEEN_MAX_ENTRIES` reasoning); one entry is about 56 bytes, so ten is roughly
 *  0.6KB. Only the newest few announcements can ever be popup candidates, so older entries
 *  are dead weight. */
export const POPUP_DISMISSED_MAX_ENTRIES = 10;

/** sessionStorage key for the once-per-browser-session flag. Prevents nagging and nothing else:
 *  no other behaviour reads it. */
export const POPUP_SESSION_KEY = 'steward.announcementPopupShown';

function ts(iso: string | null | undefined): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : t;
}

/**
 * The announcement to pop up, or null. Newest visible item that is `popup`, inside its
 * `popupUntil`, in the viewer's audience, and not already dismissed.
 *
 * `viewerAudience` is the server's answer for the active workspace, never a client guess, so a
 * family-only item cannot pop up in a firm workspace. An unparseable `popupUntil` fails closed
 * (no popup): an expiry nobody can read must not become "never expires".
 */
export function popupCandidate(
  items: readonly Announcement[],
  opts: {
    isWhiteLabel: boolean;
    viewerAudience: 'families' | 'firms';
    dismissed: AnnouncementPopupDismissed | undefined;
    now: number;
  },
): Announcement | null {
  const { isWhiteLabel, viewerAudience, dismissed, now } = opts;
  const eligible = sortAnnouncements(visibleAnnouncements(items, isWhiteLabel)).filter(a => {
    if (!a.popup) return false;
    if (!a.audience.includes(viewerAudience)) return false;
    if (dismissed && Object.prototype.hasOwnProperty.call(dismissed, a.id)) return false;
    if (a.popupUntil !== null && a.popupUntil !== undefined) {
      const until = ts(a.popupUntil);
      if (until === null || until <= now) return false;
    }
    return true;
  });
  return eligible[0] ?? null;
}

/**
 * The map after dismissing `id`: the new entry pinned, the rest newest first, truncated to the
 * cap (oldest pruned). Appcore replaces the whole value on write, so what is returned is
 * exactly what ends up stored.
 */
export function markPopupDismissed(
  dismissed: AnnouncementPopupDismissed | undefined,
  id: string,
  nowIso: string,
  max: number = POPUP_DISMISSED_MAX_ENTRIES,
): AnnouncementPopupDismissed {
  const rest = Object.entries(dismissed ?? {})
    .filter(([k]) => k !== id)
    .sort(([, a], [, b]) => (ts(b) ?? 0) - (ts(a) ?? 0));
  return Object.fromEntries([[id, nowIso] as [string, string], ...rest].slice(0, Math.max(1, max)));
}

/** Has a popup already opened in this browser session? Storage that throws reads as "no"; the
 *  flag only prevents nagging, so failing open is harmless (the dismissed map still holds). */
export function popupShownThisSession(): boolean {
  try {
    return window.sessionStorage.getItem(POPUP_SESSION_KEY) === '1';
  } catch {
    return false;
  }
}

export function markPopupShownThisSession(): void {
  try {
    window.sessionStorage.setItem(POPUP_SESSION_KEY, '1');
  } catch { /* private window or blocked storage: the flag is a convenience */ }
}

/** The quiet-moment predicate's DOM half: nothing modal is on screen. */
export function noModalOpen(doc: Document = document): boolean {
  return doc.querySelector('[aria-modal="true"]') === null;
}
